import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Card,
  Flex,
  FormLabel,
  HStack,
  IconButton,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { Restricted } from "@/app/components/Restricted";
import { DeleteIconButton } from "@/common/components/CommonIconButtons";
import { CopyInput } from "@/common/components/CopyInput";
import { DelayedSpinner } from "@/common/components/DelayedSpinner";
import FormCancel from "@/common/components/Form/FormCancel";
import FormInput from "@/common/components/Form/FormInput";
import { FormRootError } from "@/common/components/Form/FormRootError";
import FormSubmit from "@/common/components/Form/FormSubmit";
import FormSwitch from "@/common/components/Form/FormSwitch";
import { IcoRotate, IcoWarning } from "@/common/components/Icons";
import InfoIconTooltip from "@/common/components/InfoIconTooltip";
import QueryDataGuard from "@/common/components/QueryDataGuard";
import { SectionHeading } from "@/common/components/SectionHeading";
import TimestampCell from "@/common/components/TimestampCell";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useFormMutationSubmitHandler } from "@/common/hooks/useApiForm";
import { EnvironmentDisplayName } from "@/environment/components/EnvironmentDisplayName";
import { useEnvironmentData } from "@/environment/data/useEnvironmentData";
import { useEnvironmentsData } from "@/environment/data/useEnvironmentsData";
import { useSDKKeyRotateMutation } from "@/environment/data/useSDKKeyRotateMutation";
import { ListLayout } from "@/global-settings/components/GlobalSettingsHelpers";
import { useAPIKeyCreateMutation } from "@/global-settings/data/useAPIKeyCreateMutation";
import { useAPIKeyDeleteMutation } from "@/global-settings/data/useAPIKeyDeleteMutation";
import { useAPIKeysData } from "@/global-settings/data/useAPIKeysData";
import { APIKey, APIKeyWithKey } from "@reflag/shared/apiKeyAPI";
import { APIScope } from "@reflag/shared/permissions";
import { z } from "@reflag/shared/utils/zod";

type DeleteAPIKeyButtonProps = {
  apiKey: APIKey;
};

function DeleteAPIKeyButton({ apiKey }: DeleteAPIKeyButtonProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef(null);
  const deleteMutation = useAPIKeyDeleteMutation();

  const handleDelete = () => {
    deleteMutation.mutate({ apiKeyId: apiKey.id });
    onClose();
  };

  return (
    <>
      <DeleteIconButton
        isLoading={deleteMutation.isPending}
        label="Delete API key"
        size="sm"
        onClick={onOpen}
      />

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Delete API key</AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete the API key{" "}
              <Text as="span" fontWeight="medium">
                {apiKey.name}
              </Text>
              ? This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <ButtonGroup>
                <Button
                  ref={cancelRef}
                  isDisabled={deleteMutation.isPending}
                  variant="ghostDimmed"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  isLoading={deleteMutation.isPending}
                  onClick={handleDelete}
                >
                  Delete
                </Button>
              </ButtonGroup>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}

function ScopeBadge({ scope }: { scope: APIScope }) {
  return (
    <Badge size="sm" variant="outline">
      {scope}
    </Badge>
  );
}

function APIKeysTable() {
  const { appId } = useEnvironment();
  const { data: apiKeys = [], isLoading } = useAPIKeysData(appId);

  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [recentlyCreatedKey, setRecentlyCreatedKey] = useState<{
    id: string;
    key: string;
  } | null>(null);

  const handleCreateSuccess = (apiKey: APIKeyWithKey) => {
    setRecentlyCreatedKey({
      id: apiKey.id,
      key: apiKey.key,
    });
  };

  return (
    <Restricted alertStyle="callout" role="admin">
      {isLoading ? (
        <Flex justify="center" p={8}>
          <DelayedSpinner />
        </Flex>
      ) : (
        <Flex direction="column" gap={2}>
          {!!apiKeys.length && (
            <Table size="sm" variant="list">
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Scope</Th>
                  <Th>Created</Th>
                  <Th>Last used</Th>
                  <Th>Key</Th>
                  <Th w="0"></Th>
                </Tr>
              </Thead>
              <Tbody>
                {apiKeys.map((apiKey) => {
                  const canCopyKey = recentlyCreatedKey?.id === apiKey.id;
                  return (
                    <Tr key={apiKey.id}>
                      <Td>
                        <Text fontWeight="medium">{apiKey.name}</Text>
                      </Td>
                      <Td>
                        <Flex gap={2}>
                          {apiKey.scopes.map((scope) => (
                            <ScopeBadge key={scope} scope={scope} />
                          ))}
                        </Flex>
                      </Td>
                      <Td>
                        <TimestampCell fontSize="sm" value={apiKey.createdAt} />
                      </Td>
                      <Td>
                        {apiKey.lastUsedAt ? (
                          <TimestampCell
                            fontSize="sm"
                            value={apiKey.lastUsedAt}
                          />
                        ) : (
                          <Text color="dimmed" fontSize="sm">
                            Never
                          </Text>
                        )}
                      </Td>
                      <Td>
                        <Text fontFamily="code">{apiKey.keyObfuscated}</Text>
                      </Td>
                      <Td>
                        <Flex gap={2} justify="flex-end">
                          {canCopyKey && (
                            <CopyInput
                              buttonLabel={`Copy API key`}
                              showValue={false}
                              value={recentlyCreatedKey.key}
                            />
                          )}
                          <DeleteAPIKeyButton apiKey={apiKey} />
                        </Flex>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          )}
          <Flex justify="flex-start">
            <Button variant="outline" onClick={() => setIsCreateFormOpen(true)}>
              New API key
            </Button>
          </Flex>
          <CreateAPIKeyForm
            appId={appId}
            isOpen={isCreateFormOpen}
            onClose={() => setIsCreateFormOpen(false)}
            onSuccess={handleCreateSuccess}
          />
        </Flex>
      )}
    </Restricted>
  );
}

const CreateAPIKeyFormSchema = z.object({
  name: z.string().min(1, "Give the key a descriptive name"),
  scopes: z.object({
    read: z.boolean(),
    createFlag: z.boolean(),
    updateFlagTargeting: z.boolean(),
    writeEntities: z.boolean(),
  }),
});

type CreateAPIKeyFormData = z.infer<typeof CreateAPIKeyFormSchema>;
type CreateAPIKeyFormScope = keyof CreateAPIKeyFormData["scopes"];

const scopeMap: Record<
  CreateAPIKeyFormScope,
  { default: boolean; scope: APIScope; description: string }
> = {
  read: {
    default: true,
    scope: "read",
    description: "Read-only access to all app data (required)",
  },
  createFlag: {
    default: false,
    scope: "create:flag",
    description: "Allow creating flags",
  },
  updateFlagTargeting: {
    default: false,
    scope: "write:flag:targeting",
    description: "Can update flag targeting (all environments)",
  },
  writeEntities: {
    default: false,
    scope: "write:entities",
    description: "Create, update, and delete companies and users",
  },
};

function CreateAPIKeyForm({
  appId,
  isOpen,
  onClose,
  onSuccess,
}: {
  appId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (apiKey: APIKeyWithKey) => void;
}) {
  const createMutation = useAPIKeyCreateMutation();

  const form = useForm<CreateAPIKeyFormData>({
    resolver: zodResolver(CreateAPIKeyFormSchema),
    defaultValues: {
      name: "",
      scopes: Object.fromEntries(
        Object.entries(scopeMap).map(([scope, { default: defaultValue }]) => [
          scope,
          defaultValue,
        ]),
      ) as CreateAPIKeyFormData["scopes"],
    },
  });

  const handleClose = () => {
    form.reset();
    createMutation.reset();
    onClose();
  };

  const handleSubmit = useFormMutationSubmitHandler(
    form,
    createMutation,
    (result) => {
      onSuccess?.(result.apiKey);
    },
    {
      successToast: "API key created",
      errorToast: "Failed to create API key",
      prepareVariables: async (data: CreateAPIKeyFormData) => {
        const scopes = Object.entries(data.scopes)
          .filter(([_, enabled]) => enabled)
          .map(([scope]) => scopeMap[scope as CreateAPIKeyFormScope].scope);

        return {
          name: data.name,
          appId,
          scopes,
        };
      },
    },
  );

  return (
    <Modal
      closeOnOverlayClick={createMutation.isSuccess ? false : true}
      isOpen={isOpen}
      size="md"
      onClose={handleClose}
    >
      <ModalOverlay />
      <ModalContent maxWidth="lg">
        <FormProvider {...form}>
          <form onSubmit={handleSubmit}>
            <ModalHeader>Create API key</ModalHeader>
            <ModalCloseButton />
            <ModalBody py={4}>
              {createMutation.isSuccess ? (
                <Flex direction="column" gap={2}>
                  <Alert status="warning">
                    <AlertIcon>
                      <IcoWarning size="lg" />
                    </AlertIcon>
                    <AlertDescription>
                      Save the key, it will only be shown once.
                    </AlertDescription>
                  </Alert>
                  <Card p={4} px={6} variant="filled">
                    <CopyInput
                      buttonLabel={`Copy API key`}
                      fontFamily="code"
                      value={createMutation.data?.apiKey.key}
                    />
                  </Card>
                </Flex>
              ) : (
                <Flex direction="column" gap={4}>
                  <FormInput label="Name" name="name" autoFocus />
                  <Flex direction="column" gap={2}>
                    <FormLabel mb={0}>Scopes</FormLabel>
                    {Object.entries(scopeMap).map(
                      ([scope, { description }]) => (
                        <Flex key={scope} direction="column" gap={1}>
                          <FormSwitch
                            isDisabled={scope === "read"}
                            label={
                              <HStack>
                                <ScopeBadge
                                  scope={
                                    scopeMap[scope as CreateAPIKeyFormScope]
                                      .scope
                                  }
                                />
                                <Text color="dimmed" fontSize="xs">
                                  {description}
                                </Text>
                              </HStack>
                            }
                            name={`scopes.${scope}`}
                            size={"sm"}
                          />
                        </Flex>
                      ),
                    )}
                  </Flex>
                  <FormRootError />
                </Flex>
              )}
            </ModalBody>

            <ModalFooter>
              {createMutation.isSuccess ? (
                <ButtonGroup>
                  <FormCancel onClick={handleClose}>Close</FormCancel>
                </ButtonGroup>
              ) : (
                <ButtonGroup>
                  <FormCancel onClick={handleClose} />
                  <FormSubmit>Create</FormSubmit>
                </ButtonGroup>
              )}
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  );
}

interface RotateKeyButtonProps {
  envId: string;
  envName: string;
  keyType: "publishable" | "secret";
}

function RotateKeyButton({ envId, envName, keyType }: RotateKeyButtonProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef(null);
  const rotateMutation = useSDKKeyRotateMutation();

  const handleRotate = () => {
    rotateMutation.mutate(
      { envId, keyType },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  };

  const keyTypeLabel = keyType === "publishable" ? "publishable" : "secret";

  return (
    <>
      <Tooltip label={`Rotate ${keyTypeLabel} key`}>
        <IconButton
          aria-label={`Rotate ${envName.toLowerCase()} ${keyTypeLabel} key`}
          borderRadius="md"
          color="dimmed"
          icon={<IcoRotate size="sm" />}
          isLoading={rotateMutation.isPending}
          p={0}
          size="xs"
          variant="ghost"
          onClick={onOpen}
        />
      </Tooltip>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Rotate {keyTypeLabel} key</AlertDialogHeader>
            <AlertDialogBody>
              <Flex direction="column" gap={3}>
                <Text>
                  Are you sure you want to rotate the {keyTypeLabel} key for{" "}
                  <Text as="span" fontWeight="medium">
                    {envName}
                  </Text>
                  ?
                </Text>
                <Alert status="warning">
                  <AlertIcon>
                    <IcoWarning size="lg" />
                  </AlertIcon>
                  <AlertDescription>
                    The current key will stop working immediately. Make sure to
                    update your application with the new key.
                  </AlertDescription>
                </Alert>
              </Flex>
            </AlertDialogBody>
            <AlertDialogFooter>
              <ButtonGroup>
                <Button
                  ref={cancelRef}
                  isDisabled={rotateMutation.isPending}
                  variant="ghostDimmed"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  isLoading={rotateMutation.isPending}
                  onClick={handleRotate}
                >
                  Rotate key
                </Button>
              </ButtonGroup>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}

interface EnvironmentKeysCardProps {
  envId: string;
}

function EnvironmentKeysCard({ envId }: EnvironmentKeysCardProps) {
  const environmentDataQuery = useEnvironmentData(envId);

  return (
    <QueryDataGuard
      query={environmentDataQuery}
      render={({ name, sdkAccess }) => (
        <Flex direction="column" gap={2}>
          <HStack h={6} spacing={1}>
            <CopyInput
              _inputGroup={{ flex: 1 }}
              buttonLabel={`Copy ${name.toLowerCase()} publishable key`}
              flex={1}
              fontFamily="code"
              value={sdkAccess.publishableKey}
            />
            <Restricted alertStyle="tooltip" role="admin">
              <RotateKeyButton
                envId={envId}
                envName={name}
                keyType="publishable"
              />
            </Restricted>
          </HStack>
          <HStack h={6} spacing={1}>
            <CopyInput
              _inputGroup={{ flex: 1 }}
              buttonLabel={`Copy ${name.toLowerCase()} secret key`}
              flex={1}
              fontFamily="code"
              value={sdkAccess.secretKey}
            />
            <Restricted alertStyle="tooltip" role="admin">
              <RotateKeyButton envId={envId} envName={name} keyType="secret" />
            </Restricted>
          </HStack>
        </Flex>
      )}
    />
  );
}

function EnvironmentKeysTable() {
  const { appId } = useEnvironment();
  const { data: envs = [], isLoading } = useEnvironmentsData({ appId });

  return isLoading ? (
    <Flex justify="center" p={8}>
      <DelayedSpinner />
    </Flex>
  ) : (
    <Flex direction="column" gap={2}>
      <ListLayout templateColumns="1fr 160px 320px" showAsHeader>
        <Box>Environment</Box>
        <Box>Type</Box>
        <Box>Key</Box>
      </ListLayout>
      {envs.map((env) => (
        <ListLayout
          key={env.id}
          py={3}
          templateColumns="1fr 160px 320px"
          showAsCard
        >
          <Flex direction="column" gap={2}>
            <EnvironmentDisplayName environment={env} fontSize="md" h={6} />
          </Flex>
          <Flex direction="column" gap={2}>
            <HStack h={6} spacing={1}>
              <Text color="dimmed" fontSize="sm">
                Publishable key
              </Text>
              <InfoIconTooltip text="Used for client-side evaluation of flags in browsers." />
            </HStack>
            <HStack h={6} spacing={1}>
              <Text color="dimmed" fontSize="sm">
                Secret key
              </Text>
              <InfoIconTooltip text="Used for server-side evaluation of flags. Flags marked as secret are only exposed with a secret key." />
            </HStack>
          </Flex>
          <EnvironmentKeysCard envId={env.id} />
        </ListLayout>
      ))}
    </Flex>
  );
}

export function APIAccessManagement() {
  return (
    <Flex direction="column" gap={6} maxW="wideForm">
      <Flex direction="column" gap={4}>
        <SectionHeading>SDK keys</SectionHeading>
        <Flex align="center">
          <Text color="dimmed" fontSize="sm">
            Use with the Reflag SDK to evaluate flags in a user context. The
            flag evaluation is specific to an environment.{" "}
            <Link
              href="https://docs.reflag.com/supported-languages/overview"
              target="_blank"
            >
              Learn more
            </Link>
          </Text>
        </Flex>
        <EnvironmentKeysTable />
      </Flex>
      <Flex direction="column" gap={4}>
        <SectionHeading>API keys</SectionHeading>
        <Text color="dimmed" fontSize="sm">
          Allows for secure access to Reflag&apos;s API. Used for authentication
          in automated environments like CI/CD pipelines and background coding
          agents.{" "}
          <Link
            href="https://docs.reflag.com/api/cli#using-in-ci-cd-pipelines-beta"
            target="_blank"
          >
            Learn more
          </Link>
        </Text>
        <APIKeysTable />
      </Flex>
    </Flex>
  );
}
